import { blake2b } from '@noble/hashes/blake2b';
import { bytesToHex } from '@noble/hashes/utils';
import { WalletError } from '@cef-ai/wallet-api-client';
import type { Chain, Identity } from '@cef-ai/wallet-identity';
import type { HostToPopup, PopupToHost } from '@cef-ai/wallet';
import type { PendingConsentStore } from '~/stores';

/**
 * Sign handler needs popup-only access to the session vault's `sign(chain, bytes)`
 * and `isOpen()` (to detect a bearer-active, keyless session before prompting
 * for consent). `IdentityImpl` exposes this via `getVault()` (popup-side only — host code
 * receives a stripped `Identity` shape via the SDK). Other `Identity` implementations
 * (e.g. test fakes) may not expose it, so the dep type is widened to optional
 * here and the absence is reported as a typed `internal` error rather than a
 * raw TypeError.
 */
export interface VaultSigner {
  sign: (chain: Chain, payload: Uint8Array) => Promise<Uint8Array>;
  isOpen: () => boolean;
}
export interface SignHandlerDeps {
  identity: Identity & { getVault?: () => VaultSigner };
  pendingConsentStore: PendingConsentStore;
  send: (msg: PopupToHost) => void;
}

/** Short, display-friendly digest of the payload being signed. */
function payloadDigest(payload: Uint8Array): string {
  return `0x${bytesToHex(blake2b(payload, { dkLen: 32 }))}`;
}

export function createSignHandler(deps: SignHandlerDeps) {
  return async (msg: Extract<HostToPopup, { type: 'wallet:sign' }>, meta: { origin: string }): Promise<void> => {
    if (!deps.identity.isAuthenticated) {
      throw new WalletError('unauthorized', 'sign: not logged in');
    }
    if (!deps.identity.getVault) {
      throw new WalletError('internal', 'sign: identity does not expose a vault');
    }
    const vault = deps.identity.getVault();
    // Bearer-active (keyless) session: no signing keys. Fail fast with a typed
    // needs-step-up BEFORE prompting for consent, so the SDK can step up and
    // retry with a single consent prompt (no spurious consent on the dead attempt).
    if (!vault.isOpen()) {
      throw new WalletError('needs-step-up', 'sign: session has no keys; step up required');
    }

    // Extrinsic signing authorizes a blockchain transaction — gate it behind
    // explicit, per-request consent (best-effort: show the caller's
    // humanReadable description if present, else the payload digest). Other
    // request kinds (e.g. 'sdk' data signing) keep their existing flow.
    if (msg.requestKind === 'extrinsic') {
      const decision = await deps.pendingConsentStore.request({
        kind: 'sign',
        hostOrigin: meta.origin,
        signKind: msg.requestKind,
        humanReadable: msg.humanReadable,
        payloadDigest: payloadDigest(msg.payload),
      });
      if (decision.kind === 'deny') {
        throw new WalletError('user-cancelled', 'sign: user denied the signing request');
      }
    }

    const signature = await vault.sign(msg.chain, msg.payload);
    deps.send({
      type: 'wallet:result',
      id: msg.id,
      result: signature,
    });
  };
}
